import { Container } from "unstated";
import { t } from "@lingui/macro";
import { i18n } from "idc-ui-react";
import moment from "moment";
import {
  Notifications,
  ApiCall,
  LoggedInUser,
  Urls,
  getPreferredLanguage,
  setPreferredLanguage
} from "idc-ui-react";

const urls = Urls("Dashboard");

class Main extends Container {
  constructor() {
    super();
    this.state = {
      notifications: [],
      systems: [],
      ous: {},
      reports: [],
      loggedInUser: {},
      isFetchingReport: false,
      HTMLReport: "",
      language: getPreferredLanguage(),
      abortController: null
    };
    this.urls = urls;
    this.notifications = new Notifications({
      onNotificationsChanged: () => this._getNotificationDetails()
    });
    this._getNotificationDetails();
    this.apiCall = new ApiCall({
      urls,
      logger: console.log,
      notifications: this.notifications
    });
    this.profileURL = this.apiCall.urls.api.me;

    this.loggedInUser = new LoggedInUser({
      apiCall: this.apiCall,
      onChanged: () => this.getLoggedInUser(),
      addDefaultApps: true
    });
  }

  // Language manipulation
  setLanguage(lang) {
    setPreferredLanguage(lang);
    this.setState({ language: lang });
  }

  getInitialData = () => {
    this.getAllReports();
  };

  getLoggedInUser = async firstAttempt => {
    const loggedInUser = await this.loggedInUser.getLoggedInUser(firstAttempt);
    // TODO: Report module should be returned from BE when all IDC apps are made as modules
    // Report module
    const reportModule = {
      id: 5,
      name: "Rapporter",
      identifier: "reports",
      icon: "BIDashboard",
      link: "/reports",
      sortOrder: 5,
      menus: [
        {
          endpoint: "/reports",
          iconName: "BIDashboard",
          menuTitle: "Rapporter",
          sortOrder: 1
        }
      ]
    };
    loggedInUser.modules.push(reportModule);

    // TODO: Quick links should be returned from BE
    // Quick Links to Dashboard
    loggedInUser.settings = {
      quickLinks: [
        {
          id: 1,
          name: i18n._(t`Reports`),
          icon: "BIDashboard",
          link: "/reports",
          attention: false
        },
        {
          id: 2,
          name: "",
          attention: false
        },
        {
          id: 3,
          name: "",
          attention: false
        },
        {
          id: 4,
          name: "",
          attention: false
        },
        {
          id: 5,
          name: "",
          attention: false
        }
      ]
    };
    await this._getLoggedInUserInfo();
    await this.setState({
      loggedInUser: { ...loggedInUser }
    });
  };

  // Update loggedInUser data
  updateLoggedInUser = async newLoggedInUser => {
    this.loggedInUser.user = newLoggedInUser;
    this.setState({ loggedInUser: newLoggedInUser });
  };

  // --------------------------------------------------------------------------
  // AUTH
  // Only allow the user to edit or delete a role if they are system owners
  isAuthO = (action, item) => {
    if (!item) return false;
    const { systems, employees } = this.state;

    // Check if the user is a System Owner for the system in question.
    function userIsSystemOwner(systemName) {
      if (!systemName) return true;
      if (!systems) return false;

      const systemNames = systems.map(system => system.name);
      return systemNames.includes(systemName);
    }

    // Check if the user has access to employees.
    function userIsRoleAssigner() {
      if (!employees || employees.length === 0) return false;
      return true;
    }
    // Currently there are only restrictions on the new, edit and delete actions.
    switch (action) {
      case "edit":
        return userIsSystemOwner(item.itSystemName);
      case "delete":
        return userIsSystemOwner(item.itSystemName);
      case "assign":
        return userIsRoleAssigner();
      case "new":
        // If the user gets no systems from the API, the user isn't system
        // owner for any systems, and is therefore not authorized.
        return !!systems;
      default:
        return true;
    }
  };

  // --------------------------------------------------------------------------
  // Help functions
  // Assign option values
  _getOptions(item) {
    if (item.options === undefined || item.options === null) return item;

    const options = item.options.split(",");
    return {
      ...item,
      readOnly: options.find(i => i === "READ_ONLY") ? true : false,
      static: options.find(i => i === "STATIC") ? true : false,
      markedForDeletion: options.find(i => i === "MARKED_FOR_DELETION")
        ? true
        : false,
      disableDelete: options.find(i => i === "DISABLE_DELETE") ? true : false,
      userOnly: options.find(i => i === "USER_ONLY") ? true : false,
      activated: options.find(i => i === "QUARANTINE")
        ? true
        : options.find(i => i === "ACTIVATED")
        ? true
        : false,
      quarantine: options.find(i => i === "QUARANTINE") ? true : false,
      notActivated: options.find(i => i === "QUARANTINE")
        ? false
        : options.find(i => i === "ACTIVATED")
        ? false
        : true,
      unmapped: options.find(i => i === "UNMAPPED") ? true : false,
      status: options.find(i => i === "MARKED_FOR_DELETION")
        ? "DELETED"
        : options.find(i => i === "WILL_BE_ADDED")
        ? "ADDED"
        : options.find(i => i === "WILL_BE_REMOVED")
        ? "REMOVED"
        : options.find(i => i === "DENIED_APPROVAL")
        ? "DENIED"
        : options.find(i => i === "WAITING_APPROVAL")
        ? "WAITING"
        : null,
      master: options.find(i => i === "MASTER") ? true : false,
      waitingApprovals: options.find(i => i === "WAITING_APPROVAL")
        ? true
        : false,
      primary: options.find(i => i === "PRIMARY") ? true : false
    };
  }

  // Sort JSON object by sorted keys
  _sortJSONObjectByKeys(oArray, sortOrder) {
    const arr = [];
    oArray.forEach(o => {
      arr.push(
        Object.keys(o)
          .sort((firstEl, secondEl) => {
            const firstField = sortOrder.find(f => f.name === firstEl);
            const secondField = sortOrder.find(f => f.name === secondEl);
            return firstField["key"] - secondField["key"];
          })
          .reduce((r, k) => {
            r[k] = o[k];
            return r;
          }, {})
      );
    });
    return arr;
  }

  // Map JSON object names
  _mapJSONObjectNames(currentObject, nameDefinitions) {
    const newObject = Object.keys(currentObject).reduce((acc, key) => {
      const obj = nameDefinitions.find(f => f.name === key);
      acc[obj.name] = obj.showName;
      return acc;
    }, {});
    return newObject;
  }

  // Rename JSON object names from mapped names
  _renameJSONObjectKeys(objectArray, nameMappings) {
    const renamed = [];
    objectArray.forEach(o => {
      renamed.push(
        Object.keys(o).reduce((acc, key) => {
          acc[nameMappings[key]] = o[key];
          return acc;
        }, {})
      );
    });
    return renamed;
  }

  orderAuditlogCSV = async (queryParams, report) => {
    try {
      let response = {
        status: 204 /*No content*/,
        number: 0,
        totalPages: 0,
        size: 0,
        total: 0,
        processed: 0
      };
      if (
        !report ||
        (report && (!report.totalElements || report.totalElements === 0))
      ) {
        return response;
      }

      response.number = report.number;
      response.totalPages = report.totalPages;
      response.size = report.totalElements;
      response.total = report.totalElements;

      const params = [...queryParams];
      let newEndpoint = "/reports/auditlog/async";

      params.push("pagesize=" + response.size.toString());
      params.push("pageno=" + response.number.toString());

      let endpointParams = "";
      let sep = "?";
      params.forEach(p => {
        endpointParams = endpointParams + sep + p;
        sep = "&";
      });
      newEndpoint = "/reports/auditlog/async" + endpointParams;

      if (params.length && !params.some(f => f.split("=")[0] === "end")) {
        let extraParamsToReport =
          sep +
          "end=" +
          encodeURIComponent(moment().format("YYYY-MM-DD HH:mm:ss"));
        newEndpoint = newEndpoint + extraParamsToReport;
      }
      const res = await this.apiCall.get(`${this.urls.api.base}${newEndpoint}`);
      return res;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // ENDPOINTS
  // -------------------------------------------------------------------------
  getEndpoint(page, store) {
    let urls = this ? this.urls : store.urls;
    switch (page) {
      case "role":
        return urls.api.roles;
      case "user":
        return urls.api.users;
      case "organisation":
        return urls.api.ous;
      case "system":
        return urls.api.systems;
      case "reports":
        return urls.api.reports;
      default:
        return urls.api.base;
    }
  }
  // --------------------------------------------------------------------------

  getEmailReport = async endpoint => {
    try {
      const success = await this.apiCall.get(
        `${this.urls.api.base}${endpoint}`
      );

      if (!success) {
        this.notifications.setError(() =>
          i18n._(
            t`There was an error generating the report. Please contact your system administrator if the problem persist`
          )
        );
        return;
      }

      this.notifications.setSuccess(() =>
        i18n._(
          t`The report is generating and will be sent to your email once its done`
        )
      );
    } catch (error) {
      console.error(error);
      this.notifications.setError(() =>
        i18n._(
          t`There was an error generating the report. Please contact your system administrator if the problem persist`
        )
      );
    }
  };

  _getLoggedInUserInfo = async () => {
    const loggedInUser = await this.loggedInUser.getLoggedInUser();

    loggedInUser.roles =
      loggedInUser.roles &&
      loggedInUser.roles.map(role => ({
        ...role,
        itSystemName: role.itSystem.name
      }));

    if (loggedInUser.positions.administrative)
      loggedInUser.userAccount = loggedInUser.positions.administrative.map(
        position => ({
          ...this._getOptions(position),
          orgUnit: this._getOptions(position.orgUnit)
        })
      );

    if (loggedInUser.positions.employments)
      loggedInUser.positions = loggedInUser.positions.employments.map(
        position => ({
          ...this._getOptions(position),
          orgUnit: this._getOptions(position.orgUnit)
        })
      );

    this.setState({ loggedInUser });
  };

  setAsPrimaryPosition = async id => {
    await this.setState({ errorMessage: "", errorSavingItem: false });
    try {
      await this.apiCall.put(`${this.urls.api.positions}/${id}/primary`);
      this.notifications.setSuccess(() =>
        i18n._(t`Primary position updated successfully`)
      );
      return true;
    } catch (err) {
      console.error(err);
      this.setState({
        errorMessage: i18n._(
          t`Something went wrong! Please contact your system administrator if the problem persists.`
        ),
        errorSavingItem: true
      });
      return false;
    }
  };

  toggleITAccount = async () => {
    try {
      const res = await this.apiCall.post(
        `${this.urls.api.me}/itaccount/disable`
      );
      return res;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  _getNotificationDetails = () => {
    const notifications = this.notifications.getItems();
    this.setState({ notifications });
  };

  // SYSTEMS
  _getAllSystems = async () => {
    if (this.systems) return;
    this.setState({ isFetchingSystems: true });

    this.systems = await this.apiCall.get(`${this.urls.api.reports}/systems`);
    this.setState({ systems: this.systems, isFetchingSystems: false });
  };

  // ORGANISATIONS
  _getAllOrganisations = async () => {
    if (this.ous) return;
    this.setState({ isFetchingOrganisationalUnits: true });

    this.ous = await this.apiCall.get(`${this.urls.api.reports}/ous`);
    await this.setState({
      ous: this._getOusOptions([this.ous])[0],
      isFetchingOrganisationalUnits: false
    });
  };

  _getOusOptions = ous => {
    return ous.map(ou => {
      const children = ou.children.length
        ? this._getOusOptions(ou.children)
        : [];

      return { ...this._getOptions(ou), children };
    });
  };

  // REPORTS
  getAllReports = async () => {
    this.setState({ isFetchingItems: true });
    if (!this.reports) {
      this.reports = await this.apiCall.get(`${this.urls.api.reports}`);
    }

    const items =
      this.reports && this.reports.reports
        ? this.reports.reports.map(report => ({
            ...this._getOptions(report),
            id: report.reportType,
            endpoint: report.history[0] ? report.history[0].endpoint : ""
          }))
        : [];

    // If system report get allowed systems
    if (
      this.reports.reports &&
      this.reports.reports.find(r => r.reportType === "SYSTEMS")
    )
      this._getAllSystems();

    // If manager report get allowed organisations
    if (
      this.reports.reports &&
      this.reports.reports.find(r => r.reportType === "MANAGER")
    )
      this._getAllOrganisations();

    await this.setState({
      items,
      reports: this.reports,
      isFetchingItems: false
    });
  };

  getMfa = async () => {
    this.setState({ isFetchingMfa: true });
    const mfa = await this.apiCall.get(`${this.urls.api.base}/mfa`);
    this.setState({ mfaTypes: mfa, isFetchingMfa: false });
  };

  getPagingReport = async (item, pagingCountAvailable, params) => {
    this.setState({ isFetchingReport: true });
    const url = item.endpoint + "?" + params;
    const countUrl = pagingCountAvailable && item.endpoint + "/count?" + params;
    await this.getReport(url, null, null, countUrl);
  };

  getReportTotal = async endpoint => {
    this.setState({ isFetchingReportTotal: true });
    const reportCount = await this.apiCall.get(
      `${this.urls.api.base}${endpoint}`
    );
    this.setState({
      report: {
        ...this.state.report,
        totalElements: reportCount.totalElements,
        totalPages: reportCount.totalPages
      }
    });
    this.setState({ isFetchingReportTotal: false });
  };

  getReport = async (endpoint, fileType, id, countEndpoint) => {
    this.setState({
      isFetchingReport: true,
      isFetchingReportTotal: true,
      endpoint
    });

    if (this.state.abortController) {
      this.state.abortController.abort();
    }

    const abortController = new AbortController();
    this.setState({ abortController });

    try {
      if (fileType === "XLSX" || fileType === "CSV") {
        // Create a link
        const link = document.createElement("a");

        const { file, filename } = await this.apiCall.get(
          `${this.urls.api.base}${endpoint}${id ? `/${id}` : ""}`,
          { signal: abortController.signal },
          { fileType }
        );

        // Set link with the file for download
        link.href = window.URL.createObjectURL(new Blob([file]));
        link.setAttribute("download", filename);
        // Add the link to the page, click it and remove it again.
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } else if (fileType === "HTML") {
        const url = await this.apiCall.get(
          `${this.urls.api.base}${endpoint}${id ? `/${id}` : ""}`,
          { signal: abortController.signal },
          { fileType }
        );

        if (!url) return;

        // Set link with the file to be shown i browser
        this.setState({ HTMLReport: url.url, endpoint });
      } else {
        const report = await this.apiCall.get(
          `${this.urls.api.base}${endpoint}${id ? `/${id}` : ""}`,
          { signal: abortController.signal },
          { fileType: fileType ? fileType : "NONE" }
        ); //this get called in the wrong order
        if (!report) return;
        this.setState({ isFetchingReport: false, report, endpoint });
        if (countEndpoint) this.getReportTotal(countEndpoint);
        else this.setState({ isFetchingReportTotal: false });
        return true;
      }
    } catch (e) {
      if (e.name === "AbortError") return;
      console.error(e);
    }
    this.setState({ isFetchingReport: false, endpoint: null });
  };

  // Called on URL change
  getReportItem = async id => {
    this.params = [];
    this.setState({ HTMLReport: undefined });
    const item = this.state.items.find(r => r.id === id);

    await this.setState({ item });
    // XLSX, CSV and TXT is not called automatically on URL change. The user must click on a button
    if (
      item.fileType !== "XLSX" &&
      item.fileType !== "CSV" &&
      item.fileType !== "TXT"
    ) {
      if (!item.endpoint) return;
      await this.getReport(item.endpoint, item.fileType, null);
    }
  };

  _redirectFromId = async id => {
    const res = await this.apiCall.get(
      `${this.urls.api.notifications}/redirect/${id}`
    );
    this.setState({ notificationRedirectUrl: res.url });
  };
}

export const store = new Main();
